import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { KollelDtoIn, KollelStatus, UpdateKollelStatusDtoIn } from '@/models/in/KollelDtoIn';
import { usePutAuthApi } from '@/hooks/UseApiCalls';

interface KollelStatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  kollel: KollelDtoIn;
  onSuccess: () => void;
}

/**
 * Moves one kollel along its welfare-committee approval workflow (HAT-1446 phase 5). The options
 * come from the server-computed allowedNextStatuses on the kollel, mirroring
 * ProjectStatusChangeDialog exactly - the dialog never hard-codes the workflow.
 */
const KollelStatusChangeDialog = ({ open, onClose, kollel, onSuccess }: KollelStatusChangeDialogProps) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<KollelStatus | ''>('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: updateStatus, isPending } = usePutAuthApi<
    KollelDtoIn, object, UpdateKollelStatusDtoIn
  >(`management/kollels/${kollel.id}/status`);

  useEffect(() => {
    if (open) {
      setStatus(kollel.allowedNextStatuses[0] ?? '');
      setComment('');
      setError(null);
    }
  }, [open, kollel]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!status) return;

    setError(null);
    try {
      await updateStatus({ params: {}, body: { status, comment: comment.trim() || null } });
      onSuccess();
      handleClose();
    } catch (err) {
      console.error('Error updating kollel status:', err);
      setError(t('kollelsManagement.statusDialog.error'));
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={t('kollelsManagement.statusDialog.title')}
    >
      <div className="flex flex-col gap-4 p-4">
        <p className="body-md-semibold">{kollel.name}</p>

        <div className="flex flex-col gap-1">
          <label htmlFor="kollel-status" className="body-sm-semibold text-(--font-secondary)">
            {t('kollelsManagement.statusDialog.statusLabel')}
          </label>
          <select
            id="kollel-status"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={status}
            onChange={(e) => setStatus(e.target.value as KollelStatus)}
          >
            {kollel.allowedNextStatuses.map((nextStatus) => (
              <option key={nextStatus} value={nextStatus}>
                {t(`kollels.status.${nextStatus}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="kollel-status-comment" className="body-sm-semibold text-(--font-secondary)">
            {t('kollelsManagement.statusDialog.commentLabel')}
          </label>
          <textarea
            id="kollel-status-comment"
            rows={3}
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('kollelsManagement.statusDialog.commentPlaceholder')}
          />
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={handleClose} disabled={isPending}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending || !status}>
            {isPending ? t('saving') : t('kollelsManagement.statusDialog.submit')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default KollelStatusChangeDialog;
